import React from "react";
import styled from "styled-components";
import { injectIntl, defineMessages, FormattedMessage } from "react-intl";
import moment from "moment";
import DatePickerGroup from "../DatePickerGroup";
import MultiCheckBoxGroup from "../MultiCheckBoxGroup";
import { RESTRICTION_TYPE, UPDATE_ENTITY_ACTIONS, WEEK_DAYS } from "../../../Common/constants";
import FieldErrorList from "../FieldErrorList";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FormGroup } from "react-bootstrap-3";
import TimezonePicker from "react-bootstrap-timezone-picker";
import "react-bootstrap-timezone-picker/dist/react-bootstrap-timezone-picker.min.css";

const messages = defineMessages({
  stayDate: {
    id: "app.periodConditions.stayDate",
    description: "Stay dates",
    defaultMessage: "Stay dates",
  },
  startActive: {
    id: "app.periodConditions.startActive",
    description: "Stay active",
    defaultMessage: "Stay active",
  },
  endActive: {
    id: "app.periodConditions.endActive",
    description: "End active",
    defaultMessage: "End active",
  },
  bookingDate: {
    id: "app.periodConditions.bookingDate",
    description: "Stay dates",
    defaultMessage: "Stay dates",
  },
  startBookable: {
    id: "app.periodConditions.startActive",
    description: "Stay active",
    defaultMessage: "Stay active",
  },
  endBookable: {
    id: "app.periodConditions.endActive",
    description: "End active",
    defaultMessage: "End active",
  },
  weekDays: {
    id: "app.periodConditions.weekDays",
    description: "Week days (optional)",
    defaultMessage: "Weekdays (optional)",
  },
  weekDaysDescription: {
    id: "app.periodConditions.weekDaysDescription",
    description:
      "This rule will only apply if the time of the reservation occurs on any of the marked days (if all the fields are left empty it means they are enabled every day)",
    defaultMessage:
      "This rule will only apply if the time of the reservation occurs on any of the marked days (if all the fields are left empty it means they are enabled every day)",
  },
  hourRange: {
    id: "app.periodConditions.hoursRange",
    description: "Hour range",
    defaultMessage: "Hour range",
  },
  hourRangeDescription: {
    id: "app.periodConditions.hoursRangeDescription",
    description: "Hour range description",
    defaultMessage: "Hour range description",
  },
  monday: {
    id: "app.periodConditions.monday",
    description: "Monday",
    defaultMessage: "Monday",
  },
  tuesday: {
    id: "app.periodConditions.tuesday",
    description: "Tuesday",
    defaultMessage: "Tuesday",
  },
  wednesday: {
    id: "app.periodConditions.wednesday",
    description: "Wednesday",
    defaultMessage: "Wednesday",
  },
  thursday: {
    id: "app.periodConditions.thursday",
    description: "Thursday",
    defaultMessage: "Thursday",
  },
  friday: {
    id: "app.periodConditions.friday",
    description: "Friday",
    defaultMessage: "Friday",
  },
  saturday: {
    id: "app.periodConditions.saturday",
    description: "Saturday",
    defaultMessage: "Saturday",
  },
  sunday: {
    id: "app.periodConditions.sunday",
    description: "Sunday",
    defaultMessage: "Sunday",
  },
  start: {
    id: "app.periodConditions.start",
    description: "start",
    defaultMessage: "start",
  },
  end: {
    id: "app.periodConditions.end",
    description: "end",
    defaultMessage: "end",
  },
  addBookingDate: {
    id: "app.periodConditions.addBookingDate",
    description: "Add booking date",
    defaultMessage: "Add booking date",
  },
  deleteBookingDate: {
    id: "app.periodConditions.deleteBookingDate",
    description: "Remove booking date",
    defaultMessage: "Remove booking date",
  },
  endDateAfterStart: {
    id: "app.periodConditions.endDateAfterStart",
    description: "The end date must be greater than the start date and greater than today",
    defaultMessage: "The end date must be greater than the start date and greater than today",
  },
});

const restrictionTypes = [RESTRICTION_TYPE.OPEN_BOOKING];

const restrictionTranslations = {
  OPEN_BOOKING: {
    TITLE: <FormattedMessage {...messages.bookingDate} />,
    START: <FormattedMessage {...messages.startBookable} />,
    END: <FormattedMessage {...messages.endBookable} />,
  },
};

const Container = styled.div`
  a {
    cursor: pointer;
  }
`;

const DatePickerContainer = styled.div`
  fieldset {
    border-bottom: none;
  }

  .react-datepicker__input-container {
    width: 300px;
  }
`;

const DeleteBookingDateLink = styled.a`
  margin-top: 3px;
  margin-left: 15px;
`;

const DatePickerGroupStyled = styled.div`
  ${props =>
    (props && props.restrictionType === RESTRICTION_TYPE.OPEN_BOOKING) ||
    RESTRICTION_TYPE.OPEN_TRAVEL
      ? `
fieldset {
    padding-bottom: 0;
    margin-bottom: 0;
  }
`
      : undefined};
  margin-top: 20px;
`;

const WeekdayPickerContainer = styled.div`
  fieldset {
    padding-bottom: 0;
    margin-bottom: 10px;
  }
`;

const Message = styled.div``;

const MessageTitle = styled.div`
  font-weight: 600;
`;

const HourTimezoneContainer = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
`;

const StyledMultiCheckBoxGroup = styled(MultiCheckBoxGroup)`
  & .multicheckbox {
    margin-top: 0;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  & .multicheckbox {
    margin-top: 0;
  }
`;

const StyledTimezonePicker = styled(TimezonePicker)`
  // faux dropdown arrows
  & input {
    cursor: pointer;
  }

  :after {
    content: "";
    border: solid ${({ theme }) => theme.regularTextColor};
    border-width: 0 2px 2px 0;
    display: inline-block;
    padding: 3px;
    transform: rotate(45deg);
    height: 8px;
    width: 8px;
    margin-left: -20px;
    z-index: 2;
    margin-top: 10px;
  }
`;

const HourTimezone = styled(FormGroup)`
  display: flex;
  margin-top: 10px;
  margin-bottom: 10px !important;

  & > * {
    max-width: 150px;
    display: flex;
  }

  & > :not(.react-datepicker__tab-loop) {
    max-width: 100px;
    cursor: pointer;
    display: flex;
    & input {
      cursor: pointer;
    }
  }

  & > *:first-child {
    margin-right: 30px;
  }

  & > *:last-child {
    margin-left: 60px;
    max-width: 450px;
  }
`;

function OpenBooking({
  field,
  formValues,
  onChange,
  updateFormValue,
  intl,
  disabled,
  useTime = true,
}) {
  const { restrictions } = formValues;

  return (
    <Container>
      {restrictionTypes.map(restriction => {
        const restrictionValues =
          (restrictions && restrictions[RESTRICTION_TYPE.OPEN_BOOKING]) || [];

        return (
          <div>
            {restrictionValues.map((restrictionValue, key) => {
              return (
                <DatePickerContainer key={restriction + key}>
                  <div>
                    {restrictionValues.length > 1 && (
                      <DeleteBookingDateLink
                        onClick={() =>
                          updateFormValue({
                            action: UPDATE_ENTITY_ACTIONS.DELETE,
                            formValueProperties: `restrictions.${restriction}.${key}`,
                          })
                        }>
                        <FormattedMessage {...messages.deleteBookingDate} />
                      </DeleteBookingDateLink>
                    )}
                  </div>
                  <label>{restrictionTranslations[restriction].START}</label>
                  <DatePickerGroup
                    disabled={disabled}
                    value={restrictionValue && restrictionValue.startDate}
                    field={{
                      ...field,
                      fieldName: `restrictions.${restriction}.${key}.startDate`,
                      displayName: restrictionTranslations[restriction].START,
                      selectsStart: true,
                    }}
                    formValues={{
                      startDate: restrictionValue && restrictionValue.startDate,
                      endDate: restrictionValue && restrictionValue.endDate,
                    }}
                    onChange={onChange}
                  />

                  <DatePickerGroupStyled restrictionType={restriction}>
                    <label>{restrictionTranslations[restriction].END}</label>
                    <DatePickerGroup
                      disabled={disabled}
                      value={restrictionValue && restrictionValue.endDate}
                      field={{
                        ...field,
                        fieldName: `restrictions.${restriction}.${key}.endDate`,
                        displayName: restrictionTranslations[restriction].END,
                        selectsEnd: true,
                      }}
                      formValues={{
                        startDate: restrictionValue && restrictionValue.startDate,
                        endDate: restrictionValue && restrictionValue.endDate,
                      }}
                      onChange={onChange}
                    />
                    <FieldErrorList
                      errors={
                        restrictionValue &&
                        restrictionValue.endDate &&
                        (moment(restrictionValue.endDate).isBefore(restrictionValue.startDate) ||
                          moment(restrictionValue.endDate).isBefore(
                            moment(new Date()).format("YYYY-MM-DD")
                          )) && [intl.formatMessage(messages.endDateAfterStart)]
                      }
                    />
                  </DatePickerGroupStyled>
                  {useTime && (
                    <HourTimezoneContainer>
                      <Message>
                        <MessageTitle>
                          <FormattedMessage {...messages.hourRange} />
                        </MessageTitle>

                        <FormattedMessage {...messages.hourRangeDescription} />
                      </Message>

                      <HourTimezone>
                        <StyledDatePicker
                          className={`form-control`}
                          selected={restrictionValue && restrictionValue.startHour}
                          onChange={hour =>
                            onChange({
                              value: hour,
                              field: `restrictions.${restriction}.${key}.startHour`,
                            })
                          }
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={30}
                          timeCaption={intl.formatMessage(messages.start)}
                          dateFormat="HH:mm"
                          isClearable
                          timeFormat="HH:mm"
                        />
                        <StyledDatePicker
                          className={`form-control`}
                          selected={restrictionValue && restrictionValue.endHour}
                          onChange={hour =>
                            onChange({
                              value: hour,
                              field: `restrictions.${restriction}.${key}.endHour`,
                            })
                          }
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={30}
                          timeCaption={intl.formatMessage(messages.end)}
                          dateFormat="HH:mm"
                          isClearable
                          timeFormat="HH:mm"
                        />
                        <StyledTimezonePicker
                          defaultValue={restrictionValue.timeZone}
                          placeholder="Select timezone..."
                          onChange={timeZone =>
                            onChange({
                              value: timeZone,
                              field: `restrictions.${restriction}.${key}.timeZone`,
                            })
                          }
                        />
                      </HourTimezone>
                    </HourTimezoneContainer>
                  )}
                  <WeekdayPickerContainer>
                    <Message>
                      <MessageTitle>
                        <FormattedMessage {...messages.weekDays} />
                      </MessageTitle>
                      <FormattedMessage {...messages.weekDaysDescription} />
                    </Message>

                    <StyledMultiCheckBoxGroup
                      value={restrictionValue && restrictionValue.weekDays}
                      field={{
                        disabled: disabled,
                        fieldName: `restrictions.${restriction}.${key}.weekDays`,
                        options: [
                          {
                            ticker: WEEK_DAYS.MONDAY,
                            name: <FormattedMessage {...messages.monday} />,
                          },
                          {
                            ticker: WEEK_DAYS.TUESDAY,
                            name: <FormattedMessage {...messages.tuesday} />,
                          },
                          {
                            ticker: WEEK_DAYS.WEDNESDAY,
                            name: <FormattedMessage {...messages.wednesday} />,
                          },
                          {
                            ticker: WEEK_DAYS.THURSDAY,
                            name: <FormattedMessage {...messages.thursday} />,
                          },
                          {
                            ticker: WEEK_DAYS.FRIDAY,
                            name: <FormattedMessage {...messages.friday} />,
                          },
                          {
                            ticker: WEEK_DAYS.SATURDAY,
                            name: <FormattedMessage {...messages.saturday} />,
                          },
                          {
                            ticker: WEEK_DAYS.SUNDAY,
                            name: <FormattedMessage {...messages.sunday} />,
                          },
                        ],
                      }}
                      onChange={onChange}
                    />
                  </WeekdayPickerContainer>
                </DatePickerContainer>
              );
            })}
          </div>
        );
      })}
    </Container>
  );
}

export default injectIntl(OpenBooking);
